import { MAX_RAW_IMAGE_BYTES } from '../../../domains/media/utils/imageProcessor';
import { Logger } from '../../../utils/logger';

const CLIPBOARD_DT_PREFIX = 'CanvasClipboardImage';

export const MAX_CANVAS_DROP_IMAGE_FILES = 8;
export const MAX_CANVAS_DROP_PDF_FILES = 8;

const SUPPORTED_IMAGE_MIME_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);
const SUPPORTED_PDF_MIME_TYPES = new Set(['application/pdf']);

export function isSupportedCanvasImageFile(file: File): boolean {
  return SUPPORTED_IMAGE_MIME_TYPES.has((file.type || '').toLowerCase());
}

export function isSupportedCanvasPdfFile(file: File): boolean {
  const mimeType = (file.type || '').toLowerCase().trim();
  if (SUPPORTED_PDF_MIME_TYPES.has(mimeType)) return true;
  if (!mimeType) {
    return file.name.toLowerCase().trim().endsWith('.pdf');
  }
  return false;
}

export function extractFirstSupportedImageFileFromFileList(fileList: FileList | null | undefined): File | null {
  if (!fileList || fileList.length === 0) return null;
  for (let i = 0; i < fileList.length; i += 1) {
    const file = fileList.item(i);
    if (file && isSupportedCanvasImageFile(file)) {
      return file;
    }
  }
  return null;
}

function collectSupportedImageFilesFromDataTransfer(dataTransfer: DataTransfer): File[] {
  const out: File[] = [];
  const fromList = dataTransfer.files;
  if (fromList && fromList.length > 0) {
    for (let i = 0; i < fromList.length; i += 1) {
      const file = fromList.item(i);
      if (file && isSupportedCanvasImageFile(file)) out.push(file);
    }
  }
  if (out.length === 0) {
    const items = dataTransfer.items;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (!item || item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file && isSupportedCanvasImageFile(file)) out.push(file);
      }
    }
  }
  return out;
}

function collectSupportedPdfFilesFromDataTransfer(dataTransfer: DataTransfer): File[] {
  const out: File[] = [];
  const fromList = dataTransfer.files;
  if (fromList && fromList.length > 0) {
    for (let i = 0; i < fromList.length; i += 1) {
      const file = fromList.item(i);
      if (file && isSupportedCanvasPdfFile(file)) out.push(file);
    }
  }
  if (out.length === 0) {
    const items = dataTransfer.items;
    if (items && items.length > 0) {
      for (let i = 0; i < items.length; i += 1) {
        const item = items[i];
        if (!item || item.kind !== 'file') continue;
        const file = item.getAsFile();
        if (file && isSupportedCanvasPdfFile(file)) out.push(file);
      }
    }
  }
  return out;
}

export function collectSupportedImageFilesForCanvasDrop(
  dataTransfer: DataTransfer | null | undefined,
  options?: { maxFiles?: number },
): { files: File[]; skippedOverSize: number; skippedOverMaxCount: number } {
  const maxFiles = options?.maxFiles ?? MAX_CANVAS_DROP_IMAGE_FILES;
  if (!dataTransfer) {
    return { files: [], skippedOverSize: 0, skippedOverMaxCount: 0 };
  }

  const raw = collectSupportedImageFilesFromDataTransfer(dataTransfer);
  const seen = new Set<string>();
  const unique: File[] = [];
  for (const file of raw) {
    const key = `${file.name}\0${file.size}\0${file.lastModified}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(file);
  }

  const withinSize = unique.filter((f) => f.size <= MAX_RAW_IMAGE_BYTES);
  const skippedOverSize = unique.length - withinSize.length;
  const pool = withinSize;
  const skippedOverMaxCount = Math.max(0, pool.length - maxFiles);
  const files = pool.slice(0, maxFiles);

  Logger.debug(CLIPBOARD_DT_PREFIX, 'canvas drop file list', {
    rawCount: raw.length,
    uniqueCount: unique.length,
    withinSizeCount: withinSize.length,
    returnedCount: files.length,
    skippedOverSize,
    skippedOverMaxCount,
    maxFiles,
  });

  return { files, skippedOverSize, skippedOverMaxCount };
}

export function collectSupportedPdfFilesForCanvasDrop(
  dataTransfer: DataTransfer | null | undefined,
  options?: { maxFiles?: number },
): { files: File[]; skippedOverMaxCount: number } {
  const maxFiles = options?.maxFiles ?? MAX_CANVAS_DROP_PDF_FILES;
  if (!dataTransfer) {
    return { files: [], skippedOverMaxCount: 0 };
  }

  const raw = collectSupportedPdfFilesFromDataTransfer(dataTransfer);
  const seen = new Set<string>();
  const unique: File[] = [];
  for (const file of raw) {
    const key = `${file.name}\0${file.size}\0${file.lastModified}`;
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(file);
  }

  const skippedOverMaxCount = Math.max(0, unique.length - maxFiles);
  const files = unique.slice(0, maxFiles);

  Logger.debug(CLIPBOARD_DT_PREFIX, 'canvas drop PDF file list', {
    rawCount: raw.length,
    uniqueCount: unique.length,
    returnedCount: files.length,
    skippedOverMaxCount,
    maxFiles,
  });

  return { files, skippedOverMaxCount };
}

export function extractFirstSupportedImageFileFromDataTransfer(dataTransfer: DataTransfer | null | undefined): File | null {
  if (!dataTransfer) return null;
  const candidates = collectSupportedImageFilesFromDataTransfer(dataTransfer);
  if (candidates.length === 0) {
    Logger.debug(CLIPBOARD_DT_PREFIX, 'paste event DataTransfer: no supported image files', {
      fileListLength: dataTransfer.files?.length ?? 0,
      itemCount: dataTransfer.items?.length ?? 0,
    });
    return null;
  }
  const underLimit = candidates.filter((f) => f.size <= MAX_RAW_IMAGE_BYTES);
  const pool = underLimit.length > 0 ? underLimit : candidates;
  pool.sort((a, b) => a.size - b.size);
  const pick = pool[0];
  Logger.debug(CLIPBOARD_DT_PREFIX, 'paste event DataTransfer: picked file', {
    candidateCount: candidates.length,
    candidates: candidates.map((f) => ({ name: f.name, type: f.type, size: f.size })),
    underLimitCount: underLimit.length,
    limitBytes: MAX_RAW_IMAGE_BYTES,
    chosenName: pick.name,
    chosenType: pick.type,
    chosenSize: pick.size,
    usedFallbackOverLimit: underLimit.length === 0,
  });
  return pick;
}

function isAbsoluteHttpUrl(value: string): boolean {
  const raw = value.trim();
  if (!raw) return false;
  try {
    const parsed = new URL(raw);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function extractFirstUrlFromUriList(value: string): string | null {
  const lines = value
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0 && !line.startsWith('#'));
  for (const line of lines) {
    if (isAbsoluteHttpUrl(line)) return line;
  }
  return null;
}

function extractFirstImageSrcFromHtml(value: string): string | null {
  const imgTagRegex = /<img\b[^>]*>/gi;
  const srcAttrRegex = /\bsrc\s*=\s*(?:"([^"]+)"|'([^']+)'|([^"'\s>]+))/i;
  const tags = value.match(imgTagRegex) ?? [];
  for (const tag of tags) {
    const srcMatch = tag.match(srcAttrRegex);
    const src = srcMatch?.[1] ?? srcMatch?.[2] ?? srcMatch?.[3] ?? '';
    if (isAbsoluteHttpUrl(src)) return src;
  }
  return null;
}

export function extractImageUrlFromDataTransfer(
  dataTransfer: DataTransfer | null | undefined,
): string | null {
  if (!dataTransfer) return null;

  const html = dataTransfer.getData('text/html');
  const fromHtml = extractFirstImageSrcFromHtml(html);
  if (fromHtml) return fromHtml;

  const uriList = dataTransfer.getData('text/uri-list');
  const fromUriList = extractFirstUrlFromUriList(uriList);
  if (fromUriList) return fromUriList;

  const plainText = dataTransfer.getData('text/plain').trim();
  if (isAbsoluteHttpUrl(plainText)) return plainText;

  return null;
}

export function isEditableDomTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const editable = target.closest('input, textarea, [contenteditable="true"], [contenteditable=""], [contenteditable]');
  return Boolean(editable);
}
